import Accordion from '@codegouvfr/react-dsfr/Accordion';
import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import Pagination from '@codegouvfr/react-dsfr/Pagination';
import clsx from 'clsx';
import { isEmpty, mapValues, omit, omitBy } from 'lodash-es';
import type { Department } from 'maestro-shared/referential/Department';
import type { Region } from 'maestro-shared/referential/Region';
import { defaultPerPage } from 'maestro-shared/schema/commons/Pagination';
import type {
  Matrix,
  MatrixKind
} from 'maestro-shared/schema/Matrix/MatrixReferential';
import {
  type Context,
  ProgrammingPlanContext
} from 'maestro-shared/schema/ProgrammingPlan/Context';
import type { ProgrammingSubPlanId } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import { isPPVSubPlan } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import type { FindSampleOptions } from 'maestro-shared/schema/Sample/FindSampleOptions';
import { SampleCompliance } from 'maestro-shared/schema/Sample/SampleCompliance';
import type { SampleStatus } from 'maestro-shared/schema/Sample/SampleStatus';
import {
  UserRoleList,
  UserRolePermissions
} from 'maestro-shared/schema/User/UserRole';
import { isDefinedAndNotNull, toArray } from 'maestro-shared/utils/utils';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { AppPage } from 'src/components/_app/AppPage/AppPage';
import FiltersTags from 'src/components/FilterTags/FiltersTags';
import SampleCard from 'src/components/SampleCard/SampleCard';
import SampleTable from 'src/components/SampleTable/SampleTable';
import { useAuthentication } from 'src/hooks/useAuthentication';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import useWindowSize from 'src/hooks/useWindowSize';
import samplesSlice from 'src/store/reducers/samplesSlice';
import { getURLQuery } from 'src/utils/fetchUtils';
import SampleListHeader from 'src/views/SampleListView/SampleListHeader';
import SamplePrimaryFilters from 'src/views/SampleListView/SamplePrimaryFilters';
import SampleSecondaryFilters from 'src/views/SampleListView/SampleSecondaryFilters';
import { AuthenticatedAppRoutes } from '../../AppRoutes';
import food from '../../assets/illustrations/food.svg';
import { ApiClientContext } from '../../services/apiClient';
import SupportDocumentDownload from '../SampleView/DraftSample/SupportDocumentDownload';
import './SampleList.scss';
import { hasNewerLaunchedCampaign } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';

const SampleListView = () => {
  const apiClient = useContext(ApiClientContext);
  const dispatch = useAppDispatch();
  const { isMobile } = useWindowSize();

  const [searchParams, setSearchParams] = useSearchParams();
  const { year } = useParams<{ year: string }>();
  const { hasUserPermission, user, hasNationalView } = useAuthentication();
  const { findSampleOptions, sampleListDisplay } = useAppSelector(
    (state) => state.samples
  );

  const { data: programmingPlans } = apiClient.useFindProgrammingPlansQuery(
    {
      year: year ? Number(year) : undefined
    },
    { skip: !year }
  );
  const programmingPlan = useMemo(
    () => (programmingPlans?.length === 1 ? programmingPlans[0] : undefined),
    [programmingPlans]
  );

  const { data: allProgrammingPlans } = apiClient.useFindProgrammingPlansQuery(
    {}
  );

  const { data: programmingPlanDomains } =
    apiClient.useFindProgrammingPlanDomainsQuery();

  const domainLabelById = useMemo(
    () =>
      new Map(
        (programmingPlanDomains ?? []).map(({ id, label }) => [id, label])
      ),
    [programmingPlanDomains]
  );

  const hasNewerCampaign = useMemo(
    () =>
      !!programmingPlan &&
      hasNewerLaunchedCampaign(
        programmingPlan,
        allProgrammingPlans ?? [],
        domainLabelById
      ),
    [programmingPlan, allProgrammingPlans, domainLabelById]
  );

  const { data: laboratories } = apiClient.useFindLaboratoriesQuery({
    programmingPlanIds: toArray(programmingPlan?.id)
  });

  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  useEffect(() => {
    dispatch(
      samplesSlice.actions.changeFindOptions({
        programmingPlanIds:
          (searchParams.get('programmingPlanIds')?.split(',') as string[]) ??
          undefined,
        programmingSubPlanIds:
          (searchParams.get('programmingSubPlanIds')?.split(',') as
            | ProgrammingSubPlanId[]
            | undefined) ?? undefined,
        contexts:
          (searchParams.get('contexts')?.split(',') as Context[]) ?? undefined,
        regions: hasNationalView
          ? ((searchParams.get('regions')?.split(',') as Region[]) ?? undefined)
          : user?.region
            ? [user.region]
            : undefined,
        departments:
          (searchParams.get('departments')?.split(',') as Department[]) ??
          undefined,
        statuses:
          (searchParams.get('statuses')?.split(',') as SampleStatus[]) ??
          undefined,
        matrices:
          (searchParams.get('matrices')?.split(',') as Matrix[]) ?? undefined,
        matrixKinds:
          (searchParams.get('matrixKinds')?.split(',') as MatrixKind[]) ??
          undefined,
        sampledBy: searchParams.get('sampledBy')?.split(',') ?? undefined,
        sampledDate: searchParams.get('sampledDate'),
        sentDate: searchParams.get('sentDate'),
        reference: searchParams.get('reference'),
        compliance:
          SampleCompliance.safeParse(searchParams.get('compliance')).data ??
          undefined,
        laboratoryIds:
          searchParams.get('laboratoryIds')?.split(',') ?? undefined,
        withAtLeastOneResidue:
          (['true', 'false'].includes(
            searchParams.get('withAtLeastOneResidue') ?? ''
          )
            ? searchParams.get('withAtLeastOneResidue') === 'true'
            : undefined) ?? undefined,
        withAtLeastOneCopiedItem:
          (['true', 'false'].includes(
            searchParams.get('withAtLeastOneCopiedItem') ?? ''
          )
            ? searchParams.get('withAtLeastOneCopiedItem') === 'true'
            : undefined) ?? undefined,
        page: Number(searchParams.get('page')) || 1,
        perPage: defaultPerPage
      })
    );
  }, [searchParams, user?.region, sampleListDisplay]); // eslint-disable-line react-hooks/exhaustive-deps

  const canDownloadSupportDocument: boolean =
    programmingPlan?.subPlans.some((sp) => isPPVSubPlan(sp)) ?? false;

  const findSampleOptionsWithYear = useMemo(
    () => ({
      ...findSampleOptions,
      programmingPlanIds: findSampleOptions.programmingPlanIds?.length
        ? findSampleOptions.programmingPlanIds
        : programmingPlans?.map((p) => p.id)
    }),
    [findSampleOptions, programmingPlans]
  );

  const { data: samples } = apiClient.useFindSamplesQuery(
    findSampleOptionsWithYear,
    { skip: !programmingPlans }
  );
  const { data: samplesCount } = apiClient.useCountSamplesQuery(
    omit(findSampleOptionsWithYear, 'page', 'perPage'),
    { skip: !programmingPlans }
  );
  const { data: prescriptions } = apiClient.useFindPrescriptionsQuery(
    {
      programmingPlanId: programmingPlan?.id as string,
      contexts: findSampleOptions.contexts
        ? (findSampleOptions.contexts.filter(
            (context) => ProgrammingPlanContext.safeParse(context).success
          ) as ProgrammingPlanContext[])
        : undefined
    },
    {
      skip: !programmingPlan?.id
    }
  );
  const { data: samplers } = apiClient.useFindUsersQuery({
    roles: UserRoleList.filter((r) => {
      const permissions = UserRolePermissions[r];
      return (
        permissions.includes('createSample') ||
        permissions.includes('updateSample')
      );
    }),
    disabled: false
  });

  const changeFilter = (findFilter: Partial<FindSampleOptions>) => {
    const filteredParams = omit(
      omitBy(
        {
          ...mapValues(findSampleOptions, (value) => value?.toString()),
          ...mapValues(findFilter, (value) => value?.toString())
        },
        isEmpty
      ),
      ['page', 'perPage']
    );

    const urlSearchParams = new URLSearchParams(
      filteredParams as Record<string, string>
    );

    setSearchParams(urlSearchParams, { replace: true });
  };

  const newPartialSampleId = useMemo(() => crypto.randomUUID(), []);

  if (!year || !user) {
    return null;
  }

  return (
    <AppPage
      title={`Prélèvements ${year}`}
      subtitle="Consultez les dossiers des prélèvements"
      illustration={food}
      documentTitle="Liste des prélèvements"
      action={
        programmingPlan &&
        !hasNewerCampaign &&
        hasUserPermission('createSample') && (
          <div
            className={clsx('d-flex-row', 'd-flex-justify-center')}
            style={{ gap: '1rem' }}
          >
            {canDownloadSupportDocument && (
              <SupportDocumentDownload
                partialSample={{
                  id: newPartialSampleId,
                  sampler: user,
                  step: 'Draft' as const,
                  status: 'Draft' as const,
                  programmingPlanId: programmingPlan.id as string,
                  programmingSubPlanId: programmingPlan.subPlans[0]
                    ?.id as ProgrammingSubPlanId,
                  specificData: {}
                }}
                buttonPriority={'tertiary'}
                alignRight
              />
            )}
            <Button
              linkProps={{
                to: AuthenticatedAppRoutes.NewSampleRoute.link(
                  programmingPlan.year
                ),
                target: '_self'
              }}
              iconId="fr-icon-microscope-line"
            >
              Saisir un prélèvement
            </Button>
          </div>
        )
      }
    >
      {isMobile ? (
        <div>
          <Accordion
            label="Filtrer les résultats"
            className="sample-filters-accordion"
          >
            <div className={cx('fr-container')}>
              <SamplePrimaryFilters
                filters={findSampleOptions}
                onChange={changeFilter}
                programmingPlans={programmingPlans}
                samplers={samplers}
                prescriptions={prescriptions}
                currentUserId={user?.id}
              />
              {programmingPlan && (
                <SampleSecondaryFilters
                  year={Number(year)}
                  filters={findSampleOptions}
                  onChange={changeFilter}
                  programmingPlanId={programmingPlan.id}
                />
              )}
            </div>
          </Accordion>
          <div className={cx('fr-mx-2w')}>
            <FiltersTags
              title="Filtres actifs"
              filters={findSampleOptions}
              programmingPlans={programmingPlans}
              users={samplers}
              onChange={changeFilter}
              laboratories={laboratories}
            />
          </div>
        </div>
      ) : (
        <div className={clsx('white-container', cx('fr-px-5w', 'fr-py-3w'))}>
          <div className="d-flex-align-start">
            <div>
              <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
                <SamplePrimaryFilters
                  filters={findSampleOptions}
                  onChange={changeFilter}
                  programmingPlans={programmingPlans}
                  samplers={samplers}
                  prescriptions={prescriptions}
                  currentUserId={user?.id}
                />
                {isFilterExpanded && (
                  <SampleSecondaryFilters
                    year={Number(year)}
                    filters={findSampleOptions}
                    onChange={changeFilter}
                    programmingPlanId={programmingPlan?.id}
                  />
                )}
              </div>
              <FiltersTags
                title="Filtres actifs"
                filters={findSampleOptions}
                programmingPlans={programmingPlans}
                users={samplers}
                laboratories={laboratories}
                onChange={changeFilter}
              />
            </div>
            <Button
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              priority="secondary"
              className={cx('fr-ml-3w', 'fr-mt-4w')}
              style={{ minWidth: '140px', justifyContent: 'center' }}
            >
              {isFilterExpanded ? 'Fermer' : 'Plus de filtres'}
            </Button>
          </div>
        </div>
      )}
      <div
        className={clsx(
          'white-container',
          cx('fr-px-2w', 'fr-px-md-5w', 'fr-py-2w', 'fr-py-md-5w')
        )}
      >
        <div className={clsx(cx('fr-mb-2w', 'fr-mb-md-5w'), 'table-header')}>
          {
            <SampleListHeader
              findSampleOptionsWithYear={findSampleOptionsWithYear}
              changeFilter={changeFilter}
              samplesCount={samplesCount?.count}
            />
          }
        </div>
        {sampleListDisplay === 'cards' && (
          <div className={cx('fr-grid-row', 'fr-grid-row--gutters')}>
            {samples?.map((sample) => (
              <div className={cx('fr-col-12', 'fr-col-md-3')} key={sample.id}>
                <SampleCard sample={sample} />
              </div>
            ))}
          </div>
        )}
        {sampleListDisplay === 'table' && (
          <SampleTable samples={samples ?? []} />
        )}
        {isDefinedAndNotNull(samplesCount?.count) &&
          samplesCount.count > defaultPerPage && (
            <Pagination
              count={Math.floor(samplesCount.count / defaultPerPage) + 1}
              defaultPage={Number(findSampleOptions.page) || 1}
              getPageLinkProps={(page: number) => ({
                to: getURLQuery({
                  ...findSampleOptions,
                  page: page.toString()
                })
              })}
              className={cx('fr-mt-5w')}
            />
          )}
      </div>
    </AppPage>
  );
};

export default SampleListView;
